import {ReactElement, RefCallback, useCallback, useRef} from 'react';

import * as styles from './review-preview-video.scss';

/** Ant Design Image has no Video component; video preview uses Image.preview.imageRender. */
const DEFAULT_PREVIEW_VIDEO_VOLUME = 0.5;

export const PREVIEW_IMAGE_PLACEHOLDER_SRC =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const ANT_IMAGE_PREVIEW_VIDEO_SELECTOR = '.ant-image-preview-root video';

type PreviewVideoHandleType = {
    currentTime: number;
    pause: () => void;
    volume: number;
};

function isPreviewVideoHandle(value: unknown): value is PreviewVideoHandleType {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    return (
        Reflect.get(value, 'tagName') === 'VIDEO' &&
        typeof Reflect.get(value, 'pause') === 'function' &&
        typeof Reflect.get(value, 'currentTime') === 'number' &&
        typeof Reflect.get(value, 'volume') === 'number'
    );
}

function asPreviewVideoHandle(element: unknown): PreviewVideoHandleType | null {
    return isPreviewVideoHandle(element) ? element : null;
}

function resetVideoPlayback(video: PreviewVideoHandleType): null {
    video.pause();
    Reflect.set(video, 'currentTime', 0);

    return null;
}

function applyPreviewVolume(video: PreviewVideoHandleType): null {
    Reflect.set(video, 'volume', DEFAULT_PREVIEW_VIDEO_VOLUME);

    return null;
}

function renderEmptyPreviewToolbar(): null {
    return null;
}

function getAntImagePreviewVideos(): Array<PreviewVideoHandleType> {
    return [...globalThis.document.querySelectorAll(ANT_IMAGE_PREVIEW_VIDEO_SELECTOR)].reduce<
        Array<PreviewVideoHandleType>
    >((accumulator, element) => {
        const video = asPreviewVideoHandle(element);

        return video ? [...accumulator, video] : accumulator;
    }, []);
}

function stopDomPreviewVideos(): null {
    getAntImagePreviewVideos().map(resetVideoPlayback);

    return null;
}

function stopPreviewVideos(activeVideo: PreviewVideoHandleType | null): void {
    if (activeVideo) {
        resetVideoPlayback(activeVideo);
    }

    stopDomPreviewVideos();
}

type PreviewVideoPlayerPropsType = {
    src: string;
    videoRef: RefCallback<PreviewVideoHandleType | null>;
};

function PreviewVideoPlayer(props: PreviewVideoPlayerPropsType): ReactElement {
    const {src, videoRef} = props;

    return (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- user-uploaded review attachments have no captions
        <video
            autoPlay
            className={styles.ReviewPreviewVideo_player}
            controls
            disablePictureInPicture
            playsInline
            ref={(node) => videoRef(asPreviewVideoHandle(node))}
            src={src}
        />
    );
}

export function usePreviewVideoController(): {
    previewClassName: string;
    renderEmptyPreviewToolbar: typeof renderEmptyPreviewToolbar;
    renderVideo: (src: string) => ReactElement;
    stop: () => void;
    videoRef: RefCallback<PreviewVideoHandleType | null>;
} {
    const activeVideoRef = useRef<PreviewVideoHandleType | null>(null);

    const stop = useCallback((): void => {
        stopPreviewVideos(activeVideoRef.current);
        activeVideoRef.current = null;
    }, []);

    const videoRef = useCallback<RefCallback<PreviewVideoHandleType | null>>((node) => {
        activeVideoRef.current = node;

        if (node) {
            applyPreviewVolume(node);
        }
    }, []);

    const renderVideo = useCallback((src: string) => <PreviewVideoPlayer src={src} videoRef={videoRef} />, [videoRef]);

    return {
        previewClassName: styles.ReviewPreviewVideo_preview,
        renderEmptyPreviewToolbar,
        renderVideo,
        stop,
        videoRef,
    };
}
